import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import {
  Character,
  CharacterIdentity,
  AppMode,
  RageMPIdentityKey,
  CharacterFace,
  CharacterBody,
  CharacterHair,
  CharacterBeard,
} from "../character.types";
import { DEFAULT_FATHER, DEFAULT_MOM } from "../constants/parents";

const DEFAULT_CHARACTER: Character = {
  identity: {
    firstName: "",
    lastName: "",
    gender: "male",
    mother: DEFAULT_MOM,
    father: DEFAULT_FATHER,
    heritage: 0,
    similarity: 0,
    legs: 0,
    tops: 0,
    shoes: 0,
  },
  face: {
    nose_width: 0,
    nose_height: 0,
    nose_length: 0,
    nose_bridge: 0,
    nose_tip: 0,
    nose_bridge_shift: 0,
    brow_height: 0,
    neck_width: 0,
    lips: 0,
  },
  body: {
    eyes: 1,
  },
  hair: {
    hairstyle: 0,
    eyebrows: 0,
    haircolor: 0,
  },
  beard: {
    facial_hair: 0,
  },
};

// ─── RAGE MP helpers ──────────────────────────────────────────────────────────
const mp: any = {
  trigger() {},
};

const mpTrigger = (event: string, ...args: unknown[]) => {
  try {
    mp.trigger(event, ...args);
  } catch {
    console.log("[mp.trigger]", event, ...args);
  }
};

export const rageMP = {
  notifyError: (text: string) => mpTrigger("notifyError", "error", text),
  finishCharacter: (fullName: string) => mpTrigger("finishCharacter", fullName),
  finishCharacterOp: (fullName: string) =>
    mpTrigger("finishCharacter:op", fullName),
  changeCamera: (category: string) => mpTrigger("char:change:camera", category),
};

// ─── document.updateUI bridge ────────────────────────────────────────────────
// Serverul poate actualiza orice camp din identitate direct:
//   mp.callBrowser(browser, "document.updateUI('heritage', 0.4)")

declare global {
  interface Document {
    updateUI?: (key: RageMPIdentityKey, value: unknown) => void;
  }
}

// ─── Defaults ────────────────────────────────────────────────────────────────

// ─── Validare + save ─────────────────────────────────────────────────────────

const validateAndSave = (character: Character, mode: AppMode): boolean => {
  const { firstName, lastName, legs, shoes, tops } = character.identity;
  console.log(character);
  if (mode === "player") {
    if (
      !firstName ||
      firstName.length < 4 ||
      !lastName ||
      lastName.length < 4
    ) {
      rageMP.notifyError(
        "Numele si prenumele trebuie sa contina minim 3 caractere.",
      );
      return false;
    }
    if (!legs && !shoes && !tops) {
      rageMP.notifyError(
        "Nu poti finaliza caracterul deoarece nu ai selectat hainele.",
      );
      return false;
    }
  }

  const fullName = `${firstName} ${lastName}`;
  mode === "operator"
    ? rageMP.finishCharacterOp(fullName)
    : rageMP.finishCharacter(fullName);

  return true;
};

// ─── Context ─────────────────────────────────────────────────────────────────

interface CharacterContextValue {
  character: Character;
  mode: AppMode;
  handleIdentityChange: (patch: Partial<CharacterIdentity>) => void;
  handleFaceChange: (patch: Partial<CharacterFace>) => void;
  handleBodyChange: (patch: Partial<CharacterBody>) => void;
  handleHairChange: (patch: Partial<CharacterHair>) => void;
  handleBeardChange: (patch: Partial<CharacterBeard>) => void;
  onSave: () => boolean;
}

export const CharacterContext = createContext<CharacterContextValue | null>(
  null,
);

// ─── Provider ────────────────────────────────────────────────────────────────

interface CharacterProviderProps {
  children: React.ReactNode;
  initialCharacter?: Partial<CharacterIdentity>;
  mode?: AppMode; // "player" (default) | "operator"
}

export const CharacterProvider: React.FC<CharacterProviderProps> = ({
  children,
  initialCharacter = {},
  mode = "player",
}) => {
  const [character, setCharacter] = useState<Character>({
    ...DEFAULT_CHARACTER,
    ...initialCharacter,
  });

  const handleIdentityChange = useCallback(
    (patch: Partial<CharacterIdentity>) => {
      setCharacter((prev) => ({
        ...prev,
        identity: { ...prev.identity, ...patch },
      }));
    },
    [],
  );

  const handleFaceChange = useCallback((patch: Partial<CharacterFace>) => {
    setCharacter((prev) => ({
      ...prev,
      face: { ...prev.face, ...patch },
    }));
  }, []);

  const handleBodyChange = useCallback((patch: Partial<CharacterBody>) => {
    setCharacter((prev) => ({
      ...prev,
      body: { ...prev.body, ...patch },
    }));
  }, []);

  const handleHairChange = useCallback((patch: Partial<CharacterHair>) => {
    setCharacter((prev) => ({
      ...prev,
      hair: { ...prev.hair, ...patch },
    }));
  }, []);

  const handleBeardChange = useCallback((patch: Partial<CharacterBeard>) => {
    setCharacter((prev) => ({
      ...prev,
      beard: { ...prev.beard, ...patch },
    }));
  }, []);

  // Inregistreaza bridge-ul pentru server → UI updates
  useEffect(() => {
    document.updateUI = (key, value) => {
      handleIdentityChange({ [key]: value } as Partial<CharacterIdentity>);
    };
    return () => {
      delete document.updateUI;
    };
  }, [handleIdentityChange]);

  const onSave = useCallback(
    () => validateAndSave(character, mode),
    [character, mode],
  );

  const value = useMemo(
    () => ({
      character,
      mode,
      handleIdentityChange,
      onSave,
      handleFaceChange,
      handleBodyChange,
      handleHairChange,
      handleBeardChange,
    }),
    [
      character,
      mode,
      handleIdentityChange,
      onSave,
      handleFaceChange,
      handleBeardChange,
      handleBodyChange,
      handleHairChange,
    ],
  );

  return (
    <CharacterContext.Provider value={value}>
      {children}
    </CharacterContext.Provider>
  );
};
